import { useEffect, useState } from 'react'
import axios from '../../helpers/axios'

/**
 * «3D-комплект» офиса в калькуляторе: какие товары офиса стоят за деталями сцены.
 * Настраивается в «Товары → 3D-комплект». Карточка привязана к ЛИНЕЙКЕ товаров
 * («Подставка — 50…130×20×12», «Металлокерамика», «Фрезеровка — буквы/даты»):
 * нужный размер, форму, цвет, способ надписи сцена выбирает здесь.
 *
 * Размер не совпал — берём самый похожий и пишем «похожий размер: … (в сцене …)».
 * Нет привязки или нет офиса — сцена считает как раньше (цены ALOTB).
 * Явный productId детали в сцене всегда главнее привязки.
 */

let state = { officeId: '', bindings: {}, loading: false }
let version = 0
const listeners = new Set()

const emit = () => {
    version += 1
    listeners.forEach((listener) => {
        try {
            listener(version)
        } catch (_error) { }
    })
}

const loadScene3dKitRequest = async (officeId) => {
    const id = String(officeId || '')
    if (!id) {
        if (state.officeId) {
            state = { officeId: '', bindings: {}, loading: false }
            emit()
        }
        return
    }
    if (state.officeId === id && (state.loading || Object.keys(state.bindings).length)) return
    state = { officeId: id, bindings: state.officeId === id ? state.bindings : {}, loading: true }
    try {
        const { data } = await axios.get('/office/scene3d-kit', { params: { officeId: id } })
        if (state.officeId !== id) return
        state = { officeId: id, bindings: data?.bindings && typeof data.bindings === 'object' ? data.bindings : {}, loading: false }
        emit()
    } catch (_error) {
        // Нет прав / нет сети — работаем на встроенных ценах.
        if (state.officeId === id) state = { ...state, loading: false }
    }
}

// Параллельный запрос бланка ждёт ту же загрузку привязок, что и сцена.
const pendingLoads = new Map()
export const loadScene3dKit = (officeId) => {
    const id = String(officeId || '')
    if (pendingLoads.has(id)) return pendingLoads.get(id)
    const request = loadScene3dKitRequest(id).finally(() => {
        if (pendingLoads.get(id) === request) pendingLoads.delete(id)
    })
    pendingLoads.set(id, request)
    return request
}

/** После сохранения во вкладке «3D-комплект» — сразу обновить калькулятор. */
export const setScene3dKitBindings = (officeId, bindings) => {
    state = { officeId: String(officeId || ''), bindings: bindings && typeof bindings === 'object' ? bindings : {}, loading: false }
    emit()
}

export const useScene3dKitVersion = () => {
    const [value, setValue] = useState(version)
    useEffect(() => {
        listeners.add(setValue)
        return () => listeners.delete(setValue)
    }, [])
    return value
}

/* ------------------------------------------------------------------ */

const num = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}
const round2 = (value) => Math.round(num(value) * 100) / 100
const productsOf = (slot) => {
    const list = state.bindings?.[slot]?.products
    return Array.isArray(list) ? list.filter((product) => product?._id) : []
}
const titleOf = (product) => product?.fullName || product?.name || ''
const sizeText = (dims = []) => dims.filter((value) => value > 0).map((value) => Math.round(value)).join('×')

/**
 * Товар линейки под нужный размер: точный, иначе самый похожий
 * (подставка 1000×200×150 → «1000×200×120», с пометкой).
 */
export const pickBySize = (products = [], want = []) => {
    const wanted = want.map(num).filter((value) => value > 0).sort((a, b) => b - a)
    const sized = products.filter((product) => Array.isArray(product?.dims) && product.dims.length)
    if (!wanted.length || !sized.length) return products[0] ? { product: products[0], exact: !wanted.length, note: '' } : null
    let best = null
    sized.forEach((product) => {
        const dims = product.dims.map(num).sort((a, b) => b - a)
        const count = Math.min(dims.length, wanted.length)
        let score = 0
        let exact = true
        for (let i = 0; i < count; i += 1) {
            const diff = Math.abs(dims[i] - wanted[i])
            if (diff > Math.max(10, wanted[i] * 0.03)) exact = false
            // Главный размер (длина / ширина) весит больше.
            score += (diff / Math.max(1, wanted[i])) * (i === 0 ? 3 : 1)
        }
        if (!best || score < best.score) best = { product, score, exact, dims: product.dims }
    })
    if (!best) return null
    return {
        product: best.product,
        exact: best.exact,
        note: best.exact ? '' : `похожий размер: ${sizeText(best.dims)} (в сцене ${sizeText(want.map(num))})`,
    }
}

const line = (product, quantity, extra = {}) => {
    const qty = round2(quantity)
    const unitPrice = round2(product?.price)
    return {
        product,
        quantity: qty,
        unitPrice,
        total: round2(qty * unitPrice),
        title: titleOf(product),
        note: '',
        ...extra,
    }
}

/** Количество в единицах товара: шт / пог. м / м² / м³ — от размеров детали сцены (мм). */
const amountFor = (product, { pieces = 1, lengthMm = 0, widthMm = 0, heightMm = 0, perimeter = false } = {}) => {
    const unit = String(product?.unit || 'piece')
    const area = (num(lengthMm) * num(widthMm)) / 1e6
    if (unit === 'squareMeter') return area || 1
    if (unit === 'cubicMeter') return (area * num(heightMm)) / 1000 || 1
    if (unit === 'linearMeter') {
        const meters = perimeter ? (2 * (num(lengthMm) + num(widthMm))) / 1000 : Math.max(num(lengthMm), num(widthMm)) / 1000
        return meters || 1
    }
    return Math.max(1, num(pieces) || 1)
}

/* ---------------- Детали комплекта (подставка, цветник, участок) ---------------- */

/**
 * Товар офиса для строки getMonumentAssemblyCatalogLines.
 * → { product, quantity, unitPrice, total, title, note, includedInSet? } или null (цена как раньше).
 */
export const resolveKitAssemblyLine = (assemblyLine = {}) => {
    const role = String(assemblyLine?.role || '')
    const dims = assemblyLine?.dimensions || {}

    if (role === 'monument-pedestal') {
        const products = productsOf(String(assemblyLine?.variant || '') === 'bevel' ? 'pedestal-bevel' : 'pedestal-flat')
        const pick = pickBySize(products, [dims.width, dims.depth, dims.height])
        return pick ? line(pick.product, 1, { note: pick.note }) : null
    }

    if (role === 'monument-flowerbed-long-curb' || role === 'monument-flowerbed-short-curb') {
        const sets = productsOf('flowerbed-set')
        if (sets.length) {
            // Цветник комплектом: цену комплекта делим по длине поребриков —
            // видно, сколько стоит каждый, а в сумме ровно цена комплекта.
            const isShort = role === 'monument-flowerbed-short-curb'
            const longLen = isShort ? num(assemblyLine?.longLength) : num(dims.length)
            const shortLen = isShort ? num(dims.length) : num(assemblyLine?.shortLength)
            const outerWidth = num(assemblyLine?.flowerbedOuterWidth) || num(dims.width) * 2 + shortLen
            const pick = pickBySize(sets, [longLen || num(dims.length), shortLen || outerWidth])
            if (!pick) return null
            const price = round2(pick.product.price)
            const longQty = isShort ? 2 : (num(assemblyLine.quantity) || 2)
            const shortQty = isShort ? (num(assemblyLine.quantity) || 1) : 1
            const totalLength = longLen * longQty + shortLen * shortQty
            const shortTotal = longLen > 0 && shortLen > 0 && totalLength > 0
                ? round2(price * (shortLen * shortQty) / totalLength)
                : 0
            if (isShort) {
                return {
                    product: pick.product,
                    quantity: shortQty,
                    unitPrice: round2(shortTotal / shortQty),
                    total: shortTotal,
                    title: 'Поребрик короткий (комплект цветника)',
                    note: '',
                    includedInSet: shortTotal <= 0,
                }
            }
            const longTotal = round2(price - shortTotal)
            return {
                product: pick.product,
                quantity: longQty,
                unitPrice: round2(longTotal / longQty),
                total: longTotal,
                title: `${titleOf(pick.product)} — длинные поребрики`,
                note: pick.note,
            }
        }
        const extra = productsOf('curb-extra')[0]
        if (!extra) return null
        // Поребрик отдельно: за пог. м — по длине детали, иначе — за штуку.
        const pieces = num(assemblyLine.quantity) || 1
        const quantity = String(extra?.unit || 'piece') === 'linearMeter'
            ? (pieces * Math.max(num(dims.length), num(dims.width))) / 1000 || pieces
            : pieces
        return line(extra, quantity)
    }

    if (role === 'monument-ledger') {
        const pick = pickBySize(productsOf('ledger'), [dims.length, dims.width, dims.height])
        return pick ? line(pick.product, 1, { note: pick.note }) : null
    }

    // Геометрия «Заливка» не является заказом установки. Не подставляем
    // товар из старого слота install-monument: монтаж теперь выбирается
    // отдельно в «Работы на месте» (подтверждённый WRK).
    // Здесь допустим только отдельный товар на фундамент/бетон, если он
    // действительно назначен в слот foundation.
    // Монолитная прямоугольная заливка сцены не равна ленточной заливке
    // по периметру и не равна установке памятника. В офисных привязках могут
    // лежать товары разных типов: нельзя брать первый товар наугад.
    if (role === 'monument-foundation') {
        // Явно выбранный productId уже обработан вызывающим редактором.
        const suitable = productsOf('foundation').filter((product) => {
            const name = titleOf(product).toLowerCase().replace(/ё/g, 'е')
            const unit = String(product?.unit || 'piece')
            // Лента/периметр и работы по монтажу требуют отдельных 3D-типов и WRK.
            return unit !== 'linearMeter'
                && !/(?:ленточ|периметр|установк|монтаж|демонтаж|бурен|столб)/i.test(name)
        })
        // Если нет подходящего товара, оставляем геометрию «Заливка» и
        // незаполненную цену — запрещено выставлять случайный тариф.
        if (!suitable.length) return null
        const sized = suitable.filter((product) => Array.isArray(product?.dims) && product.dims.length >= 2)
        // Если несколько тарифов без понятных размеров, выбор неоднозначен.
        if (suitable.length > 1 && sized.length !== suitable.length) return null
        const candidates = sized.length ? sized : suitable
        const pick = pickBySize(candidates, [dims.width, dims.length ?? dims.depth, dims.height])
        // Для размерных товаров нужен точный размер, не «ближайшая» заливка.
        if (!pick || (sized.length && !pick.exact)) return null
        const product = pick.product
        return line(product, amountFor(product, {
            pieces: assemblyLine?.quantity,
            lengthMm: dims.length ?? dims.depth,
            widthMm: dims.width,
            heightMm: dims.height,
            perimeter: false,
        }), { note: pick.note })
    }

    // Плитка не берёт товар из «3D-комплекта»: цена считается в самом редакторе
    // плитки (цена за шт. × количество по раскладке) — как видит её менеджер.
    if (role === 'monument-tile') return null

    const one = {
        'monument-bedding': 'bedding',
        'monument-grass': 'grass',
    }[role]
    if (!one) return null
    const product = productsOf(one)[0]
    if (!product) return null
    const quantity = amountFor(product, {
        pieces: assemblyLine?.quantity,
        lengthMm: dims.length,
        widthMm: dims.width,
        heightMm: dims.height,
    })
    return line(product, quantity)
}

/* ---------------- Оформление: надписи, кресты, портреты ---------------- */

const LETTER_RE = /\p{L}/gu
const DIGIT_RE = /\d/g
const countOf = (text, re) => (String(text || '').match(re) || []).length

/** Способ надписи: гравировка (по умолчанию) / фрезеровка / сусальное золото. */
export const TEXT_METHODS = Object.freeze([
    { value: 'engraving', label: 'Гравировка' },
    { value: 'milling', label: 'Фрезеровка' },
    { value: 'gold', label: 'Сусальное золото' },
])
export const CROSS_STYLES = Object.freeze([
    { value: 'straight', label: 'Прямой' },
    { value: 'figured', label: 'Фигурный' },
    { value: 'openwork', label: 'Ажурный' },
])
export const CERAMIC_MATERIALS = Object.freeze([
    { value: 'metal', label: 'Металлокерамика' },
    { value: 'photo', label: 'Фотокерамика / ч/б стекло' },
    { value: 'glass', label: 'Портретное стекло' },
])
export const CERAMIC_FORMS = Object.freeze([
    { value: 'oval', label: 'Овал', shape: 'oval' },
    { value: 'rect', label: 'Прямоугольник', shape: 'rect' },
    { value: 'arch', label: 'Арка', shape: 'rect' },
    { value: 'plaque', label: 'Табличка с текстом', shape: 'rect' },
])
export const CERAMIC_COLORS = Object.freeze([
    { value: 'bw', label: 'Ч/б' },
    { value: 'color', label: 'Цвет' },
])

const pickChar = (products, chars, bold) => (
    products.find((product) => product?.attrs?.chars === chars && !!product?.attrs?.bold === !!bold)
    || products.find((product) => product?.attrs?.chars === chars)
    || null
)

/** Фрезеровка / золото: буквы и даты считаются отдельно, у каждого — своя цена за знак. */
const perCharLines = (slot, text, bold) => {
    const products = productsOf(slot)
    if (!products.length) return null
    const letters = countOf(text, LETTER_RE)
    const digits = countOf(text, DIGIT_RE)
    const out = []
    const letter = pickChar(products, 'letter', bold)
    const date = pickChar(products, 'date', bold) || letter
    if (letters && letter) out.push(line(letter, letters, { suffix: 'letters', note: `${letters} букв` }))
    if (digits && date) out.push(line(date, digits, { suffix: 'dates', note: `${digits} цифр` }))
    return out.length ? out : null
}

const filterBy = (products, test) => {
    const list = products.filter(test)
    return list.length ? list : products
}

/**
 * Товар(ы) офиса для оформления памятника.
 * → массив строк [{ product, quantity, unitPrice, total, title, note, suffix? }] или null (цена как раньше).
 */
export const resolveKitDecoration = (type = '', template = {}, { extraText = '' } = {}) => {
    const kind = String(type || '')
    const method = String(template?.textMethod || 'engraving')
    const bold = template?.fontBold === true

    if (kind === 'engravingText' || kind === 'epitaph') {
        const text = `${extraText ? `${extraText}\n` : ''}${template?.text || ''}`
        if (method === 'milling') return perCharLines('letters-milling', text, bold)
        if (method === 'gold') return perCharLines('letters-gold', text, bold)
        if (kind === 'engravingText') {
            const product = productsOf('text-engraving')[0]
            return product ? [line(product, 1)] : null
        }
        const product = productsOf('epitaph-engraving')[0]
        const chars = String(template?.text || '').replace(/\s+/g, '').length
        return product && chars ? [line(product, chars, { note: `${chars} знаков` })] : null
    }

    if (kind === 'engravedCross') {
        const heightMm = Math.max(num(template?.heightMm), num(template?.widthMm))
        if (method === 'engraving') {
            const products = productsOf('cross-engraving')
            if (!products.length) return null
            const big = heightMm >= 250
            const product = products.find((item) => !!item?.attrs?.a4 === big) || products[0]
            return [line(product, 1)]
        }
        const products = productsOf(method === 'gold' ? 'cross-gold' : 'cross-milling')
        if (!products.length) return null
        const style = String(template?.crossStyle || 'straight')
        const pool = filterBy(products, (item) => item?.attrs?.cross === style)
        const pick = pickBySize(pool, [heightMm <= 180 ? 120 : 300])
        return pick ? [line(pick.product, 1)] : null
    }

    if (kind === 'engravedImage') {
        // Рисунок: выбранная в оформлении позиция прайса («Цветы под датами», «Рамка»…).
        const products = productsOf('engraving-art')
        const product = products.find((item) => String(item._id) === String(template?.artProductId || ''))
        return product ? [line(product, 1)] : null
    }

    if (kind === 'artistPortrait') {
        const product = productsOf('portrait-engraving')[0]
        return product ? [line(product, 1)] : null
    }

    if (kind === 'ceramicPortrait' || kind === 'ceramicGranite') {
        const want = [template?.widthMm, template?.heightMm]
        const pool = ceramicPool(kind, template)
        if (!pool.length) return null
        const pick = pickBySize(pool, want)
        return pick ? [line(pick.product, 1, { note: pick.note })] : null
    }

    return null
}

/** Керамика / стекло нужного вида (материал, форма, одинарная/двойная, ч/б/цвет) из линейки офиса. */
const ceramicPool = (kind, template = {}) => {
    const material = kind === 'ceramicGranite' ? 'photo' : String(template?.ceramicMaterial || 'metal')
    let pool = productsOf(material === 'glass' ? 'portrait-glass' : material === 'photo' ? 'ceramic-photo' : 'ceramic-metal')
    if (!pool.length) return []
    const shape = String(template?.shape || 'oval')
    if (material === 'metal') {
        const wantShape = ['oval', 'rect', 'arch', 'plaque'].includes(template?.ceramicForm)
            ? template.ceramicForm
            : shape === 'rect' ? 'rect' : 'oval'
        pool = filterBy(pool, (item) => (item?.attrs?.shapes || []).includes(wantShape))
        const persons = Number(template?.ceramicPersons) === 2 ? 2 : 1
        pool = filterBy(pool, (item) => num(item?.attrs?.persons || 1) === persons)
        const color = template?.colorMode === 'color' ? 'color' : 'bw'
        pool = filterBy(pool, (item) => item?.attrs?.color === color)
    } else if (material === 'glass') {
        const wantShape = template?.ceramicForm === 'rect' || (!template?.ceramicForm && shape === 'rect') ? 'rect' : 'oval'
        pool = filterBy(pool, (item) => (item?.attrs?.shapes || []).includes(wantShape))
    }
    return pool
}

/**
 * Размеры керамики / стекла из прайса офиса — для выбора в оформлении.
 * → [{ key, widthMm, heightMm, price, title, label }] (пусто — линейка не привязана).
 */
export const listKitCeramicSizes = (kind = '', template = {}) => {
    if (kind !== 'ceramicPortrait' && kind !== 'ceramicGranite') return []
    const seen = new Set()
    return ceramicPool(kind, template)
        .filter((product) => Array.isArray(product?.dims) && product.dims.length >= 2)
        .map((product) => {
            const [a, b] = product.dims.map(num)
            const widthMm = Math.min(a, b)
            const heightMm = Math.max(a, b)
            return {
                key: `${widthMm}x${heightMm}`,
                widthMm,
                heightMm,
                price: round2(product.price),
                title: titleOf(product),
                label: `${widthMm / 10}×${heightMm / 10} см — ${round2(product.price).toLocaleString('ru-RU')} ₽`,
            }
        })
        .filter((item) => {
            if (seen.has(item.key)) return false
            seen.add(item.key)
            return true
        })
        .sort((x, y) => x.widthMm * x.heightMm - y.widthMm * y.heightMm)
}

/** Цена оформления по товарам офиса: { total, lines } или null (нет привязки — цена ALOTB). */
export const priceKitDecoration = (type, template, options) => {
    const lines = resolveKitDecoration(type, template, options)
    if (!lines?.length) return null
    return { total: round2(lines.reduce((sum, item) => sum + num(item.total), 0)), lines }
}

/** Рисунки из прайса офиса (гравировка: цветы, рамка, пейзаж…) — для выбора в оформлении. */
export const listKitArtOptions = () => productsOf('engraving-art').map((product) => ({
    id: String(product._id),
    title: titleOf(product),
    price: round2(product.price),
}))
